import { useCallback, useRef, type PointerEvent as ReactPointerEvent } from "react";

type Accent = "teal" | "violet" | "sun";

/** Filled-track colour per accent — the chunky "pop" bar. */
const FILL: Record<Accent, string> = {
  teal: "bg-teal",
  violet: "bg-violet",
  sun: "bg-sun",
};

/**
 * A custom, fully-controlled slider built from a div track + a draggable thumb.
 * Deliberately NOT an <input type="range"> so it can wear the site's pop
 * language (thick ink border, hard offset shadow) and behave identically on
 * touch. Supports pointer drag, click-to-jump, and full keyboard control.
 */
export function Slider({
  value,
  min,
  max,
  step = 1,
  onChange,
  label,
  format,
  accent = "teal",
  ariaLabel,
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  label?: string;
  format?: (v: number) => string;
  accent?: Accent;
  ariaLabel?: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);

  // Clamp to range and snap to the nearest step.
  const clampSnap = useCallback(
    (raw: number) => {
      const clamped = Math.min(max, Math.max(min, raw));
      const snapped = Math.round((clamped - min) / step) * step + min;
      // Kill floating-point fuzz introduced by fractional steps (e.g. 0.5).
      const fixed = Number(snapped.toFixed(6));
      return Math.min(max, Math.max(min, fixed));
    },
    [min, max, step]
  );

  // Map a clientX to a snapped value using the track's geometry.
  const valueFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el) return value;
      const rect = el.getBoundingClientRect();
      const ratio = rect.width ? (clientX - rect.left) / rect.width : 0;
      return clampSnap(min + ratio * (max - min));
    },
    [clampSnap, min, max, value]
  );

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture?.(e.pointerId);
    // preventDefault suppresses the implicit focus, so focus the track manually
    // to keep keyboard control working right after a click/tap.
    el.focus?.();
    onChange(valueFromClientX(e.clientX));
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Only track while the pointer is captured (i.e. actively dragging).
    if (!(e.currentTarget as HTMLElement).hasPointerCapture?.(e.pointerId)) return;
    onChange(valueFromClientX(e.clientX));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    let next: number | null = null;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowUp":
        next = value + step;
        break;
      case "ArrowLeft":
      case "ArrowDown":
        next = value - step;
        break;
      case "Home":
        next = min;
        break;
      case "End":
        next = max;
        break;
      case "PageUp":
        next = value + step * 5;
        break;
      case "PageDown":
        next = value - step * 5;
        break;
    }
    if (next !== null) {
      e.preventDefault();
      onChange(clampSnap(next));
    }
  };

  const pct = max === min ? 0 : ((value - min) / (max - min)) * 100;
  const display = format ? format(value) : String(value);

  return (
    <div className="w-full">
      {(label || format) && (
        <div className="mb-3 flex items-center justify-between gap-3">
          {label && (
            <span className="font-display text-sm font-extrabold text-navy">
              {label}
            </span>
          )}
          {format && (
            <span className="chip num !border-navy bg-cream">{display}</span>
          )}
        </div>
      )}

      {/* Generous vertical padding gives the thumb a >=44px touch target while
          the visible bar stays slim. */}
      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-label={ariaLabel ?? label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={display}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onKeyDown={handleKeyDown}
        className="group relative flex touch-none cursor-pointer select-none items-center py-3 focus-visible:outline-none"
      >
        {/* Bar */}
        <div className="relative h-3.5 w-full rounded-full border-[3px] border-navy bg-white">
          {/* Filled portion */}
          <div
            className={`absolute inset-y-0 left-0 rounded-full ${FILL[accent]}`}
            style={{ width: `${pct}%` }}
          />
        </div>

        {/* Thumb — centred on the value; the outer group ring shows on focus. */}
        <div
          className="pointer-events-none absolute top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-navy bg-white shadow-pop-xs transition-shadow group-focus-visible:ring-2 group-focus-visible:ring-teal group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-cream cursor-grab active:cursor-grabbing"
          style={{ left: `${pct}%` }}
        >
          <span
            className={`absolute inset-1.5 rounded-full ${FILL[accent]}`}
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
}
